import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, Users, Package, Plane, Wallet, AlertTriangle,
  Clock, Target, FileDown, Calendar, BarChart3, PieChart as PieIcon,
  RefreshCw, Plus, Pencil, Trash2, DollarSign, Activity,
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { logger } from '../../utils/logger';
import { caymanToday, formatDate } from '../../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0d9488', '#06b6d4', '#0284c7', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#64748b', '#14b8a6'];

const fmtCI = (n) => `CI$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMonth = (k) => {
  const [y, m] = k.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};

export const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const authHeaders = useMemo(() => ({ Authorization: `Basic ${btoa(':' + password)}` }), [password]);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const currentYear = new Date().getFullYear();
  const [pnlYear, setPnlYear] = useState(currentYear);
  const [pnlMonth, setPnlMonth] = useState('');
  const [pnl, setPnl] = useState(null);
  const [expenseRows, setExpenseRows] = useState([]);
  const [expenseForm, setExpenseForm] = useState(null);
  const [expenseYear, setExpenseYear] = useState(currentYear);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const endpoints = [
        ['revenueTrend', '/api/admin/analytics/revenue-trend?months=12'],
        ['payCycles', '/api/admin/analytics/pay-cycles?cycles=6'],
        ['topClients', '/api/admin/analytics/top-clients?limit=10'],
        ['services', '/api/admin/analytics/service-lines'],
        ['routes', '/api/admin/analytics/routes'],
        ['forecast', '/api/admin/analytics/cashflow-forecast'],
        ['aging', '/api/admin/analytics/aging-receivables'],
        ['daysToPay', '/api/admin/analytics/days-to-pay'],
        ['funnel', '/api/admin/analytics/quote-funnel'],
        ['yoy', '/api/admin/analytics/yoy'],
        ['kpis', '/api/admin/analytics/kpis'],
        ['retention', '/api/admin/analytics/retention'],
        ['profitByReloc', '/api/admin/analytics/profit-per-relocation'],
      ];
      const results = await Promise.all(
        endpoints.map(([, url]) =>
          fetch(`${API_URL}${url}`, { headers: authHeaders })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      );
      const next = {};
      endpoints.forEach(([k], i) => { next[k] = results[i]; });
      setData(next);
      await loadPnl(pnlYear, pnlMonth);
      await loadExpenses(expenseYear);
    } catch (err) {
      logger.error('analytics load', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadPnl = async (y, m) => {
    try {
      const url = m
        ? `${API_URL}/api/admin/analytics/pnl?year=${y}&month=${m}`
        : `${API_URL}/api/admin/analytics/pnl?year=${y}`;
      const r = await fetch(url, { headers: authHeaders });
      if (r.ok) setPnl(await r.json());
    } catch (e) { logger.error('pnl', e); }
  };

  const loadExpenses = async (y) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/expenses?year=${y}`, { headers: authHeaders });
      if (r.ok) setExpenseRows((await r.json()).items || []);
    } catch (e) { logger.error('expenses', e); }
  };

  const saveExpense = async (form) => {
    const isEdit = !!form.id;
    const url = isEdit
      ? `${API_URL}/api/admin/expenses/${form.id}`
      : `${API_URL}/api/admin/expenses`;
    const res = await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setExpenseForm(null);
      await loadExpenses(expenseYear);
      await loadPnl(pnlYear, pnlMonth);
    } else alert('Save failed');
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    const r = await fetch(`${API_URL}/api/admin/expenses/${id}`, { method: 'DELETE', headers: authHeaders });
    if (r.ok) { await loadExpenses(expenseYear); await loadPnl(pnlYear, pnlMonth); }
  };

  const downloadCsv = async (path, filename) => {
    const r = await fetch(`${API_URL}${path}`, { headers: authHeaders });
    if (!r.ok) return alert('Export failed');
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const kpis = data.kpis || {};
  const yoy = data.yoy || {};
  const funnel = data.funnel || {};
  const forecast = data.forecast || {};
  const aging = data.aging || {};
  const retention = data.retention || {};
  const revenueMonths = (data.revenueTrend?.months || []);
  const services = data.services?.service_lines || [];
  const routes = data.routes?.routes || [];
  const topClients = data.topClients?.clients || [];
  const daysToPay = data.daysToPay || {};
  const profitByReloc = data.profitByReloc?.relocations || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" /> Analytics &amp; Finance
              </h1>
              <p className="text-xs text-foreground/60">Revenue, cashflow, KPIs and exports</p>
            </div>
          </div>
          <button
            onClick={loadAll}
            disabled={refreshing}
            data-testid="refresh-analytics"
            className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-1 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* KPI TILES */}
        <section>
          <SectionTitle icon={Target} title="Key performance indicators" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiTile label="Avg quote value" value={fmtCI(kpis.avg_quote_value)} icon={DollarSign} />
            <KpiTile label="Acceptance rate" value={`${kpis.acceptance_rate_pct || 0}%`} icon={Target} tone="emerald" />
            <KpiTile label="Repeat-client %" value={`${kpis.repeat_client_pct || 0}%`} icon={Users} tone="cyan" />
            <KpiTile label="Revenue per pet" value={fmtCI(kpis.revenue_per_pet)} icon={Plane} tone="indigo" />
          </div>
        </section>

        {/* REVENUE TREND */}
        <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
          <SectionTitle icon={TrendingUp} title="Revenue trend (last 12 months)" />
          {revenueMonths.length > 0 ? (
            <>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart data={revenueMonths}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0d9488" stopOpacity={0.7} />
                        <stop offset="95%" stopColor="#0d9488" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v/1000}k`} />
                    <Tooltip formatter={(v) => fmtCI(v)} labelFormatter={fmtMonth} />
                    <Area type="monotone" dataKey="total" stroke="#0d9488" fillOpacity={1} fill="url(#rev)" name="Total revenue" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {/* YoY summary */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-[11px] text-foreground/60 uppercase tracking-wide">YTD {yoy.this_year}</div>
                  <div className="font-bold text-foreground">{fmtCI(yoy.ytd_this)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-[11px] text-foreground/60 uppercase tracking-wide">YTD {yoy.last_year}</div>
                  <div className="font-bold text-foreground">{fmtCI(yoy.ytd_last)}</div>
                </div>
                {yoy.ytd_growth_pct !== null && yoy.ytd_growth_pct !== undefined && (
                  <div className={`rounded-lg p-3 ${yoy.ytd_growth_pct >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <div className="text-[11px] text-foreground/60 uppercase tracking-wide">YoY growth</div>
                    <div className={`font-bold ${yoy.ytd_growth_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {yoy.ytd_growth_pct >= 0 ? '▲' : '▼'} {Math.abs(yoy.ytd_growth_pct)}%
                    </div>
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-[11px] text-foreground/60 uppercase tracking-wide">Full {yoy.last_year}</div>
                  <div className="font-bold text-foreground">{fmtCI(yoy.full_year_last)}</div>
                </div>
              </div>
            </>
          ) : <EmptyState>No revenue yet.</EmptyState>}
        </section>

        {/* PAY CYCLES (16th → 15th) */}
        <section className="bg-white border border-border rounded-xl p-6 shadow-sm" data-testid="analytics-pay-cycles">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <SectionTitle icon={Calendar} title="Pay cycles (16th – 15th)" />
            <button
              onClick={() => navigate('/admin/pay-cycles')}
              className="text-xs px-3 py-1 border border-primary text-primary rounded hover:bg-primary/10"
              data-testid="open-pay-cycles-page"
            >
              Manage all cycles &rarr;
            </button>
          </div>
          {(data.payCycles?.cycles || []).length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-foreground/60 uppercase">
                  <th className="py-2">Cycle</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Target</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Quote pay</th>
                  <th className="text-right">Receipts</th>
                  <th className="text-center"># Pay</th>
                </tr></thead>
                <tbody>
                  {(data.payCycles?.cycles || []).map((c) => {
                    const statusBg = { on_track: 'bg-emerald-50/60', near: 'bg-amber-50/60', behind: 'bg-red-50/40' };
                    const statusBadge = { on_track: 'bg-emerald-200 text-emerald-900', near: 'bg-amber-200 text-amber-900', behind: 'bg-red-200 text-red-900' };
                    const statusLabel = { on_track: 'On track', near: 'Near', behind: 'Behind' };
                    return (
                      <tr key={c.cycle_key} className={(statusBg[c.status] || '') + ' ' + (c.is_current ? 'bg-amber-50/40 ' : '') + 'border-t border-border'}>
                        <td className="py-2">
                          {c.label}
                          {c.is_current && <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-200 text-amber-900 rounded px-1">In progress</span>}
                          {c.historical_override && <span className="ml-2 text-[10px] uppercase tracking-wide bg-blue-200 text-blue-900 rounded px-1">Historical</span>}
                        </td>
                        <td className="text-right font-semibold text-foreground">{fmtCI(c.total)}</td>
                        <td className="text-right text-foreground/70">{c.target > 0 ? fmtCI(c.target) : '—'}</td>
                        <td className="text-center">
                          {c.target_pct != null ? (
                            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide rounded font-semibold ${statusBadge[c.status] || 'bg-gray-200 text-gray-700'}`}>
                              {statusLabel[c.status] || '—'} · {c.target_pct.toFixed(0)}%
                            </span>
                          ) : <span className="text-[10px] text-foreground/40">No target</span>}
                        </td>
                        <td className="text-right text-foreground/70">{fmtCI(c.invoice_revenue)}</td>
                        <td className="text-right text-foreground/70">{fmtCI(c.quote_payment_revenue)}</td>
                        <td className="text-right text-foreground/70">{fmtCI(c.receipt_revenue)}</td>
                        <td className="text-center text-foreground/70">{c.payments_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <EmptyState>No pay cycles yet.</EmptyState>}
        </section>

        {/* CASHFLOW FORECAST + AGING */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <SectionTitle icon={Activity} title="Cashflow forecast" />
            <p className="text-xs text-foreground/60 mb-3">Expected cash from pending invoices + outstanding quote balances.</p>
            <div className="space-y-2">
              <ForecastRow label="Next 30 days" value={forecast.d30} tone="emerald" />
              <ForecastRow label="30–60 days" value={forecast.d60} tone="teal" />
              <ForecastRow label="60–90 days" value={forecast.d90} tone="cyan" />
              <ForecastRow label="90+ days" value={forecast.d90_plus} tone="slate" />
              <ForecastRow label="No due date" value={forecast.no_due_date} tone="amber" />
            </div>
          </section>

          <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <SectionTitle icon={AlertTriangle} title="Aging receivables" />
            <p className="text-xs text-foreground/60 mb-3">How overdue your outstanding balances are.</p>
            <div className="space-y-2">
              {['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'].map((k) => (
                <AgingRow key={k} bucket={aging[k]} highlight={k === 'd61_90' || k === 'd90_plus'} />
              ))}
            </div>
          </section>
        </div>

        {/* QUOTE FUNNEL */}
        <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
          <SectionTitle icon={Target} title="Quote conversion funnel" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <FunnelStep label="Total quotes" value={funnel.total_quotes} />
            <FunnelStep label="Sent" value={funnel.sent} sub={`${funnel.sent_rate_pct || 0}%`} />
            <FunnelStep label="Accepted" value={funnel.accepted} sub={`${funnel.acceptance_rate_pct || 0}% of sent`} tone="emerald" />
            <FunnelStep label="Paid / Converted" value={funnel.converted_to_invoice} sub={`${funnel.conversion_rate_pct || 0}% conversion`} tone="teal" />
          </div>
        </section>

        {/* TOP CLIENTS + DAYS TO PAY */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <SectionTitle icon={Users} title="Top clients by revenue" />
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-foreground/60 uppercase">
                  <th className="py-2">Client</th><th>Jobs</th><th className="text-right">Total</th>
                </tr></thead>
                <tbody>
                  {topClients.map((c) => (
                    <tr key={c.email} className="border-t border-border">
                      <td className="py-2">
                        <div className="font-medium text-foreground">{c.name}</div>
                        <div className="text-xs text-foreground/50">{c.email}</div>
                      </td>
                      <td>{(c.invoices || 0) + (c.quote_payments || 0) + (c.receipts || 0)}</td>
                      <td className="text-right font-semibold text-primary">{fmtCI(c.total)}</td>
                    </tr>
                  ))}
                  {topClients.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-sm text-foreground/50">No paying clients yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <SectionTitle icon={Clock} title="Days-to-pay" />
            <div className="bg-gray-50 rounded-lg p-3 mb-3 flex items-center justify-between">
              <div>
                <div className="text-xs text-foreground/60">Overall average</div>
                <div className="text-2xl font-bold text-foreground">{daysToPay.overall_avg_days ?? 0} days</div>
              </div>
              <div className="text-xs text-foreground/60 text-right">
                Based on {daysToPay.paid_invoice_count || 0} paid invoices
              </div>
            </div>
            <div className="overflow-auto max-h-80">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-foreground/60 uppercase">
                  <th className="py-2">Client</th><th>Invoices</th><th className="text-right">Avg days</th>
                </tr></thead>
                <tbody>
                  {(daysToPay.per_client || []).map((r) => (
                    <tr key={r.client_email} className="border-t border-border">
                      <td className="py-2 truncate">{r.client_name}</td>
                      <td>{r.invoices}</td>
                      <td className={`text-right font-semibold ${r.avg_days > 30 ? 'text-amber-600' : 'text-foreground/80'}`}>
                        {r.avg_days}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* SERVICE LINES + ROUTES */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <SectionTitle icon={PieIcon} title="Revenue by service type" />
            {services.length > 0 ? (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={services} dataKey="revenue" nameKey="category" outerRadius={90} label={({ category }) => category}>
                      {services.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtCI(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : <EmptyState>No paid invoices yet.</EmptyState>}
          </section>

          <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <SectionTitle icon={Plane} title="Revenue by route (origin → destination)" />
            {routes.length > 0 ? (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={routes.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v/1000}k`} />
                    <YAxis type="category" dataKey="route" width={160} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtCI(v)} />
                    <Bar dataKey="revenue" fill="#0d9488" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <EmptyState>No route data yet.</EmptyState>}
          </section>
        </div>

        {/* RETENTION */}
        <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
          <SectionTitle icon={Users} title="Client retention" />
          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatPill label="New (1 booking)" value={retention.new_client_count} />
            <StatPill label="Repeat (2+)" value={retention.repeat_client_count} tone="emerald" />
            <StatPill label="Dormant (12+ months)" value={retention.dormant_client_count} tone="amber" />
          </div>
          {(retention.dormant_clients || []).length > 0 && (
            <details className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <summary className="cursor-pointer text-sm font-medium text-amber-800">
                Re-engage dormant clients ({retention.dormant_clients.length})
              </summary>
              <ul className="mt-2 text-xs text-amber-900 space-y-1">
                {retention.dormant_clients.slice(0, 10).map((c) => (
                  <li key={c.email} className="flex justify-between">
                    <span>{c.name} — last booked {formatDate(c.last_booking)}</span>
                    <span className="font-medium">{fmtCI(c.total_spent)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        {/* P&L + EXPENSES */}
        <section className="bg-white border border-border rounded-xl p-6 shadow-sm" data-testid="pnl-section">
          <SectionTitle icon={FileDown} title="Profit & loss statement" />
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <LabeledField label="Year">
              <input type="number" value={pnlYear} onChange={(e) => { const v = Number(e.target.value); setPnlYear(v); loadPnl(v, pnlMonth); }} className="px-3 py-2 border border-border rounded-lg w-28" />
            </LabeledField>
            <LabeledField label="Month (optional)">
              <select value={pnlMonth} onChange={(e) => { setPnlMonth(e.target.value); loadPnl(pnlYear, e.target.value); }} className="px-3 py-2 border border-border rounded-lg">
                <option value="">All year</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>{new Date(2020, m - 1).toLocaleDateString('en-GB', { month: 'long' })}</option>
                ))}
              </select>
            </LabeledField>
          </div>
          {pnl && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <SummaryCard label="Revenue" value={fmtCI(pnl.total_revenue)} />
              <SummaryCard label="Expenses" value={fmtCI(pnl.total_expenses)} tone="red" />
              <SummaryCard label="Net profit" value={fmtCI(pnl.net_profit)} tone={pnl.net_profit >= 0 ? 'emerald' : 'red'} />
              <SummaryCard label="Margin" value={`${pnl.margin_pct || 0}%`} tone="indigo" />
            </div>
          )}
          {pnl && Object.keys(pnl.expenses_by_category || {}).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-foreground/70 mb-2">Expense breakdown</p>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                {Object.entries(pnl.expenses_by_category).map(([cat, amt]) => (
                  <div key={cat} className="flex justify-between"><span>{cat}</span><span className="font-medium">{fmtCI(amt)}</span></div>
                ))}
              </div>
            </div>
          )}

          {/* Expense manager */}
          <div className="mt-6 border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Business expenses</h3>
              <div className="flex items-center gap-2">
                <select value={expenseYear} onChange={(e) => { const y = Number(e.target.value); setExpenseYear(y); loadExpenses(y); }} className="px-2 py-1 text-xs border border-border rounded">
                  {Array.from({ length: 4 }, (_, i) => currentYear - i).map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <button
                  onClick={() => setExpenseForm({ expense_date: caymanToday(), amount: '', category: 'Airline Fees', description: '' })}
                  data-testid="add-expense-btn"
                  className="px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Add expense
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-80">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-foreground/60 uppercase">
                  <th className="py-2">Date</th><th>Category</th><th>Description</th><th>Vendor</th><th className="text-right">Amount</th><th></th>
                </tr></thead>
                <tbody>
                  {expenseRows.map((e) => (
                    <tr key={e.id} className="border-t border-border">
                      <td className="py-2">{formatDate(e.expense_date)}</td>
                      <td>{e.category}</td>
                      <td className="truncate max-w-xs">{e.description}</td>
                      <td className="text-foreground/60">{e.vendor || '—'}</td>
                      <td className="text-right font-semibold">{fmtCI(e.amount)}</td>
                      <td className="text-right">
                        <button onClick={() => setExpenseForm(e)} className="p-1 hover:text-primary" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteExpense(e.id)} className="p-1 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                  {expenseRows.length === 0 && (
                    <tr><td colSpan={6} className="py-6 text-center text-sm text-foreground/50">No expenses for {expenseYear}. Add your first to track true profit.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* PROFIT PER RELOCATION */}
        <section className="bg-white border border-border rounded-xl p-6 shadow-sm">
          <SectionTitle icon={Package} title="Profit per relocation" />
          <p className="text-xs text-foreground/60 mb-3">Revenue minus expenses tagged to each relocation job.</p>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] text-foreground/60 uppercase">
                <th className="py-2">Pet</th><th>Client</th><th>Route</th>
                <th className="text-right">Revenue</th><th className="text-right">Expenses</th>
                <th className="text-right">Profit</th><th className="text-right">Margin</th>
              </tr></thead>
              <tbody>
                {profitByReloc.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="py-2">{r.pet_name}</td>
                    <td className="text-foreground/70 truncate max-w-[150px]">{r.client_name}</td>
                    <td className="text-xs text-foreground/50 truncate">{r.origin} → {r.destination}</td>
                    <td className="text-right">{fmtCI(r.revenue)}</td>
                    <td className="text-right text-red-600">{fmtCI(r.expenses)}</td>
                    <td className={`text-right font-semibold ${r.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtCI(r.profit)}</td>
                    <td className="text-right">{r.margin_pct != null ? `${r.margin_pct}%` : '—'}</td>
                  </tr>
                ))}
                {profitByReloc.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-sm text-foreground/50">No relocations yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        {/* EXPORTS */}
        <section className="bg-white border border-border rounded-xl p-6 shadow-sm" data-testid="exports-section">
          <SectionTitle icon={FileDown} title="Exports for accountant / bookkeeping" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ExportButton label="Invoices CSV" onClick={() => downloadCsv('/api/admin/analytics/export/invoices.csv', 'invoices.csv')} />
            <ExportButton label="Payments CSV" onClick={() => downloadCsv('/api/admin/analytics/export/payments.csv', 'payments.csv')} />
            <ExportButton label="Quotes CSV" onClick={() => downloadCsv('/api/admin/analytics/export/quotes.csv', 'quotes.csv')} />
            <ExportButton label="Clients CSV" onClick={() => downloadCsv('/api/admin/analytics/export/clients.csv', 'clients.csv')} />
            <ExportButton label={`Receipts ZIP (${currentYear})`} onClick={() => downloadCsv(`/api/admin/analytics/export/receipts-bundle/${currentYear}`, `receipts-${currentYear}.zip`)} />
            <ExportButton label={`Receipts ZIP (${currentYear - 1})`} onClick={() => downloadCsv(`/api/admin/analytics/export/receipts-bundle/${currentYear - 1}`, `receipts-${currentYear - 1}.zip`)} />
          </div>
        </section>
      </div>

      {/* Expense modal */}
      {expenseForm && (
        <ExpenseModal
          expense={expenseForm}
          onCancel={() => setExpenseForm(null)}
          onSave={saveExpense}
        />
      )}
    </div>
  );
};

// =========================================================
// Small presentation helpers
// =========================================================
const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon className="w-4 h-4 text-primary" />
    <h2 className="text-base font-semibold text-foreground">{title}</h2>
  </div>
);

const EmptyState = ({ children }) => (
  <div className="py-10 text-center text-sm text-foreground/50">{children}</div>
);

const KpiTile = ({ label, value, icon: Icon, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-primary/10 text-primary',
    emerald: 'bg-emerald-100 text-emerald-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    indigo: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-2 text-xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-foreground/60">{label}</div>
    </div>
  );
};

const ForecastRow = ({ label, value, tone }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700',
    teal: 'bg-teal-50 text-teal-700',
    cyan: 'bg-cyan-50 text-cyan-700',
    slate: 'bg-slate-50 text-slate-700',
    amber: 'bg-amber-50 text-amber-700',
  };
  return (
    <div className={`flex items-center justify-between rounded-lg p-3 ${tones[tone]}`}>
      <span className="text-sm font-medium">{label}</span>
      <span className="text-base font-bold">{fmtCI(value)}</span>
    </div>
  );
};

const AgingRow = ({ bucket, highlight }) => {
  if (!bucket) return null;
  return (
    <div className={`flex items-center justify-between rounded-lg p-3 border ${highlight && bucket.amount > 0 ? 'border-red-200 bg-red-50' : 'border-border bg-gray-50'}`}>
      <div>
        <div className="text-sm font-medium text-foreground">{bucket.label}</div>
        <div className="text-[11px] text-foreground/60">{bucket.count || 0} invoice(s)</div>
      </div>
      <span className={`text-base font-bold ${highlight && bucket.amount > 0 ? 'text-red-700' : 'text-foreground'}`}>
        {fmtCI(bucket.amount)}
      </span>
    </div>
  );
};

const FunnelStep = ({ label, value, sub, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-primary/10 text-primary border-primary/20',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className="text-xs opacity-80 uppercase tracking-wide">{label}</div>
      <div className="text-3xl font-bold">{value ?? 0}</div>
      {sub && <div className="text-xs mt-1 opacity-80">{sub}</div>}
    </div>
  );
};

const StatPill = ({ label, value, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-primary/10 text-primary',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
  };
  return (
    <div className={`rounded-lg p-3 text-center ${tones[tone]}`}>
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-xs mt-0.5">{label}</div>
    </div>
  );
};

const SummaryCard = ({ label, value, tone = 'neutral' }) => {
  const tones = {
    neutral: 'bg-gray-50 text-foreground',
    emerald: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };
  return (
    <div className={`rounded-lg p-3 ${tones[tone]}`}>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
};

const ExportButton = ({ label, onClick }) => (
  <button onClick={onClick} className="px-4 py-3 border border-border rounded-lg bg-white hover:bg-gray-50 text-sm flex items-center justify-center gap-2">
    <FileDown className="w-4 h-4" /> {label}
  </button>
);

const LabeledField = ({ label, children }) => (
  <div>
    <label className="block text-[11px] text-foreground/60 mb-1">{label}</label>
    {children}
  </div>
);

const ExpenseModal = ({ expense, onCancel, onSave }) => {
  const [form, setForm] = useState({
    id: expense.id || null,
    amount: expense.amount || '',
    category: expense.category || 'Airline Fees',
    description: expense.description || '',
    vendor: expense.vendor || '',
    expense_date: expense.expense_date || caymanToday(),
    notes: expense.notes || '',
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const categories = ['Airline Fees', 'Vet Fees', 'Import Permits', 'Customs', 'Fuel/Transport', 'Crate/Supplies', 'Boarding', 'Software/Subscriptions', 'Marketing', 'Admin', 'Other'];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-4">{form.id ? 'Edit' : 'Add'} expense</h3>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, amount: parseFloat(form.amount) }); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="Date">
              <input required type="date" value={form.expense_date} onChange={(e) => update('expense_date', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
            </LabeledField>
            <LabeledField label="Amount (CI$)">
              <input required type="number" step="0.01" value={form.amount} onChange={(e) => update('amount', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
            </LabeledField>
          </div>
          <LabeledField label="Category">
            <select value={form.category} onChange={(e) => update('category', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm">
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </LabeledField>
          <LabeledField label="Description">
            <input required value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="e.g. BA flight for Bella to UK" className="w-full px-3 py-2 border border-border rounded-lg text-sm" lang="en-GB" spellCheck="true" />
          </LabeledField>
          <LabeledField label="Vendor (optional)">
            <input value={form.vendor} onChange={(e) => update('vendor', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
          </LabeledField>
          <LabeledField label="Notes (optional)">
            <textarea rows={2} value={form.notes} onChange={(e) => update('notes', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg text-sm" lang="en-GB" spellCheck="true" />
          </LabeledField>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-border rounded-lg">Cancel</button>
            <button type="submit" className="px-4 py-2 text-sm bg-primary text-white rounded-lg">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
